import { Component, model, input, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { LineItemModel, DigitalSignatureValue, DigitalSignatureItem } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { SSOService } from '../../../../services/sso.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-digitalsignature-control',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, MenuModule, TooltipModule],
  templateUrl: './digitalsignature-control.component.html',
  styleUrl: './digitalsignature-control.component.css'
})
export class DigitalsignatureControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<DigitalSignatureValue>({ signatures: [] });
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  // Computed property to safely get signatures
  get signatures(): DigitalSignatureItem[] {
    const val = this.value();
    return val?.signatures || [];
  }

  private speechService = inject(SpeechService);
  private ssoService = inject(SSOService);
  private timezoneService = inject(TimezoneService);
  private validationService = inject(ValidationService);

  errors = computed(() => {
    if (!this.showError()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  // UI state
  showSignatureModal = signal(false);
  signatureText = signal('');
  signingAsOtherPerson = signal(false);

  // Add signature menu items - computed dynamically to get current user name
  addSignatureMenu = computed(() => {
    const currentUser = this.ssoService.getCurrentUser();
    const userName = this.ssoService.getPreferredUserName(currentUser);

    return [
      {
        label: `Sign as ${userName}`,
        icon: 'pi pi-user',
        command: (event: any) => {
          void this.openSignatureModal(false);
        }
      },
      {
        label: 'Sign as someone else',
        icon: 'pi pi-users',
        command: (event: any) => {
          void this.openSignatureModal(true);
        }
      }
    ];
  });

  private async getFormattedUserName(): Promise<string> {
    const resolvedUser = await this.ssoService.ensureCurrentUserProfileLoaded();
    return this.ssoService.getPreferredUserName(resolvedUser);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }

  async openSignatureModal(asOtherPerson: boolean): Promise<void> {
    this.signingAsOtherPerson.set(asOtherPerson);

    // Pre-fill with current user's name if not signing as someone else
    if (!asOtherPerson) {
      const formattedUserName = await this.getFormattedUserName();
      this.signatureText.set(formattedUserName);
    } else {
      this.signatureText.set('');
    }

    this.showSignatureModal.set(true);
  }

  addSignature(): void {
    const text = this.signatureText().trim();
    if (!text) {
      return;
    }

    const currentUser = this.ssoService.getCurrentUser();
    const deviceOwnerName = this.ssoService.getPreferredUserName(currentUser);
    const timestamp = this.timezoneService.buildTimestamp();

    const newSignature: DigitalSignatureItem = {
      signerId: currentUser?.userId,
      signerName: text,
      signerEmail: this.signingAsOtherPerson() ? undefined : currentUser?.email,
      signedAt: timestamp.timestamp,
      signedAtTimeZone: timestamp.timeZone,
      signedAtTimeZoneLabel: timestamp.timeZoneLabel,
      deviceOwner: deviceOwnerName,
      signatureText: text
    };

    // Safely get current signatures and add new one
    const currentSignatures = this.signatures;
    const updatedSignatures = [...currentSignatures, newSignature];

    this.value.set({ signatures: updatedSignatures });

    // Reset modal state
    this.showSignatureModal.set(false);
    this.signatureText.set('');
    this.signingAsOtherPerson.set(false);
  }

  removeSignature(index: number): void {
    const currentSignatures = [...this.signatures];
    currentSignatures.splice(index, 1);
    this.value.set({ signatures: currentSignatures });
  }

  cancelSignature(): void {
    this.showSignatureModal.set(false);
    this.signatureText.set('');
    this.signingAsOtherPerson.set(false);
  }

  formatDateTime(signature: DigitalSignatureItem): string {
    const storedDateTime = this.timezoneService.formatStoredDateTime(
      signature.signedAt,
      signature.signedAtTimeZone,
      signature.signedAtTimeZoneLabel
    );
    if (storedDateTime) {
      return storedDateTime;
    }

    const date = new Date(signature.signedAt);
    if (Number.isNaN(date.getTime())) {
      return signature.signedAt;
    }

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`;
  }

  getCurrentSigningAs(): string {
    if (this.signingAsOtherPerson()) {
      return this.signatureText() || 'someone else';
    }
    return this.ssoService.getPreferredUserName(this.ssoService.getCurrentUser());
  }
}
